/**
 * QorynQMark — renders the OFFICIAL Qoryn Q brand mark.
 *
 * Sources the artwork from `/qoryn-q-official.png` (public/ folder).
 * The PNG is the brand-approved cyan glyph on a fully transparent
 * background — extracted directly from the vendor-supplied PDF and
 * cropped tight to the mark's opaque bbox. No CSS filters, no
 * color remapping, no recoloring layer: the image ships as-is so the
 * console shows the exact hex values Qoryn ships in its brand kit.
 *
 * Sampled palette (informational):
 *   deepest  #12D4DD   mid  #12E2EC   bright  #14F3FB
 *
 * Glow policy (per brief):
 *   glow == 0 → NONE (no drop-shadow at all — pure alpha over black)
 *   glow  > 0 → small drop-shadow, MAX 13px blur, scaled linearly.
 *
 * Brightness policy (iteration 16, approved):
 *   The Q's own pixels are IMMUTABLE. To make the Q feel embedded in
 *   the field rather than pasted on top, we allow a wrapper-level
 *   OPACITY multiplier (never a filter, tint, blend-mode, or recolor).
 *   Callers set an ``opacity`` prop in [0.55..1.0] driven by agent
 *   state (idle=1.0 baseline · listening=0.85 · thinking=0.9 breathing
 *   · speaking=0.75 · interrupted=0.85 · disconnected=0.7). The membrane
 *   / particles / bloom carry the state-motion; the Q reads as calm
 *   presence.
 *
 * Props: {size, glow, opacity, testId, className, style}
 */

const GLOW_MAX_PX = 13;

export function QorynQMark({
    size = 200,
    glow = 0,
    opacity = 1.0,
    testId = "orb-q-mark",
    className = "",
    style = {},
}) {
    const clampedGlow = Math.max(0, Math.min(1, glow));
    const glowRadius = clampedGlow * GLOW_MAX_PX;
    const clampedOpacity = Math.max(0.4, Math.min(1.0, opacity));
    return (
        <img
            src="/qoryn-q-official.png"
            alt=""
            aria-hidden="true"
            data-testid={testId}
            data-q-opacity={clampedOpacity.toFixed(2)}
            width={size}
            height={size}
            draggable={false}
            className={className}
            style={{
                display: "block",
                width: size,
                height: size,
                objectFit: "contain",
                backgroundColor: "transparent",
                userSelect: "none",
                pointerEvents: "none",
                // Wrapper-level opacity only. Never a `filter: brightness()`
                // — that changes the mark's colour and violates the
                // immutability rule.
                opacity: clampedOpacity,
                transition: "opacity 350ms ease-out",
                filter:
                    clampedGlow > 0
                        ? `drop-shadow(0 0 ${glowRadius}px rgba(20,243,251,${0.35 * clampedGlow}))`
                        : "none",
                ...style,
            }}
        />
    );
}

export default QorynQMark;
